
import type { MetaInfo } from 'vue-meta';
import { useLanguageStore } from '@/helpers/composables/useStores';

import { makeRssMetaInfo } from '@/routing/editorial';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import UseArticle, { Provision } from '@/helpers/composables/useArticle';
import { BuyboxParam } from '@/helpers/offer-helper';
import type { ClickoutOfferDetails } from '@/interfaces/snowplow/clickout-context';

import { clickArticleLink, clickWatchItOn } from '@/pages/editorial/tracking';

import AuthorBox from '@/components/editorial/AuthorBox.vue';
import ListArticle from '@/components/editorial/ListArticle.vue';
import NewsArticle from '@/components/editorial/NewsArticle.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';

import {
	SnowplowClickoutContextGraphql,
	SnowplowPackageContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';

export default {
	components: {
		UseArticle,
		AuthorBox,
		ProviderIcon,
		PictureComp,
		NewsArticle,
		ListArticle,
	},
	metaInfo(): MetaInfo {
		return makeRssMetaInfo();
	},
	setup() {
		const { country } = useLanguageStore();

		function getOfferClickoutUrl(offer: ClickoutOfferDetails) {
			return new ClickoutUrl(offer)
				.set('uct_country', country.value)
				.set('uct_buybox', BuyboxParam.Normal)
				.toString();
		}

		/* TRACKING */
		function trackClickWatchItOn(mainObject: Provision['mainObject']) {
			if (!mainObject || mainObject.__typename === 'GenericTitleList') return;
			if (!mainObject.watchNowOffer) return;

			const offer = mainObject.watchNowOffer;
			if (offer) {
				clickWatchItOn({
					category: 'clickout',
					action: 'click',
					label: offer.monetizationType,
					property: offer.presentationType,
					value: 1,
					contexts: [
						SnowplowClickoutContextGraphql.fromProviderOffer(offer),
						new SnowplowTitleContextGraphql(mainObject.objectId, mainObject.objectType),
						new SnowplowPackageContext(offer.package.packageId, offer.package.technicalName),
					],
				});
			}
		}

		function trackContentClick(e: Event) {
			const target = e.target as HTMLElement;
			if (target.nodeName !== 'A') return;

			const property = (target as HTMLAnchorElement).href;

			clickArticleLink({
				category: 'userinteraction',
				action: 'content_clicked',
				label: property.includes('justwatch.com') ? 'internal' : 'external',
				property,
			});
		}

		return {
			getOfferClickoutUrl,
			trackClickWatchItOn,
			trackContentClick,
		};
	},
};
